const LEGACY_AUTH_TOKEN_STORAGE_KEY = "excalidraw-auth-token";
const LEGACY_AUTH_STATE_STORAGE_KEY = "excalidraw-auth-state";

export type AuthUser = {
  id: string;
  email?: string;
  emailVerified?: boolean;
  name?: string;
  roles: string[];
};

export type AuthSession = {
  user: AuthUser;
};

export type AuthResult = "error" | "login_required" | null;

type Navigate = (url: string) => void;

let currentSession: AuthSession | null = null;

const defaultNavigate: Navigate = (url) => window.location.assign(url);

const currentReturnPath = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("auth");
  return `${url.pathname}${url.search}${url.hash}`;
};

const authRoute = (action: "login" | "logout" | "silent") =>
  new URL(`/api/auth/${action}`, window.location.origin);

const clearLegacyAuthArtifacts = () => {
  window.localStorage.removeItem(LEGACY_AUTH_TOKEN_STORAGE_KEY);
  window.sessionStorage.removeItem(LEGACY_AUTH_STATE_STORAGE_KEY);
};

export const loadAuthSession = async (
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<AuthSession | null> => {
  clearLegacyAuthArtifacts();
  const response = await fetchImpl("/api/auth/me", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401 || response.status === 403) {
    currentSession = null;
    return null;
  }
  if (!response.ok) {
    throw new Error(`Auth session request failed (${response.status})`);
  }
  const body = (await response.json()) as { user?: AuthUser };
  if (!body.user || typeof body.user.id !== "string" || !body.user.id) {
    throw new Error("Auth session response is invalid");
  }
  currentSession = { user: { ...body.user, roles: body.user.roles || [] } };
  return currentSession;
};

export const getAuthSession = () => currentSession;

export const clearAuthSession = () => {
  currentSession = null;
  // Remove V1 artifacts during migration. Auth V3 tokens only exist inside
  // server-owned HttpOnly cookies and can never be read from here.
  clearLegacyAuthArtifacts();
};

export const getAuthStorageNamespace = () =>
  currentSession?.user.id || "anonymous";

export const consumeAuthResult = (): AuthResult => {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get("auth");
  const result = raw === "login_required" || raw === "error" ? raw : null;
  if (raw) {
    url.searchParams.delete("auth");
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
  return result;
};

export const redirectToSilentAuth = (navigate: Navigate = defaultNavigate) => {
  const url = authRoute("silent");
  url.searchParams.set("next", currentReturnPath());
  navigate(url.toString());
};

export const redirectToAuthLogin = (navigate: Navigate = defaultNavigate) => {
  const url = authRoute("login");
  url.searchParams.set("next", currentReturnPath());
  navigate(url.toString());
};

export const logout = (navigate: Navigate = defaultNavigate) => {
  clearAuthSession();
  navigate(authRoute("logout").toString());
};
